import { useState, useEffect, useMemo, useCallback } from "react";
import { Sidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { Box, IconButton, Typography, Tooltip, useTheme, alpha } from "@mui/material";
import { Link, useLocation } from "react-router-dom";

import { tokens } from "../../theme";

import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PeopleOutlineOutlinedIcon from "@mui/icons-material/PeopleOutlineOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import MenuOpenOutlinedIcon from "@mui/icons-material/MenuOpenOutlined";
import AirlineSeatIndividualSuiteOutlinedIcon from "@mui/icons-material/AirlineSeatIndividualSuiteOutlined";
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import LaptopMacOutlinedIcon from "@mui/icons-material/LaptopMacOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import EscalatorWarningOutlinedIcon from "@mui/icons-material/EscalatorWarningOutlined";
import FamilyRestroomOutlinedIcon from "@mui/icons-material/FamilyRestroomOutlined";
import BlindOutlinedIcon from "@mui/icons-material/BlindOutlined";
import HandymanIcon from "@mui/icons-material/Handyman";
import EnergySavingsLeafIcon from "@mui/icons-material/EnergySavingsLeaf";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const WEBMAIL_URL =
  "https://webmailweb-cpt.konsoleh.co.za/?_task=mail&_mbox=INBOX";

/**
 * Navigation grouped by what each section is actually about.
 * Add or reorder here — the sidebar, active state and auto-expand all follow.
 */
const NAV_GROUPS = [
  {
    id: "tasks",
    label: "Tasks",
    icon: <TaskAltOutlinedIcon />,
    items: [
      {
        title: "Task Manager",
        to: "/tasks/dashboard",
        icon: <ViewKanbanOutlinedIcon />,
      },
    ],
  },
  {
    id: "people",
    label: "People",
    icon: <GroupsOutlinedIcon />,
    items: [
      {
        title: "Staffs",
        to: "/admin/dashboard/staffs",
        icon: <PeopleOutlineOutlinedIcon />,
      },
      {
        title: "Students",
        to: "/admin/dashboard/students",
        icon: <EscalatorWarningOutlinedIcon />,
      },
      {
        title: "Parents / Guardians",
        to: "/admin/dashboard/parentsguardians",
        icon: <FamilyRestroomOutlinedIcon />,
      },
      {
        title: "Next of Kin",
        to: "/admin/dashboard/nextofkin",
        icon: <BlindOutlinedIcon />,
      },
    ],
  },
  {
    id: "leave",
    label: "Leave & HR",
    icon: <AirlineSeatIndividualSuiteOutlinedIcon />,
    items: [
      {
        title: "Leave Requests",
        to: "/admin/dashboard/leave",
        icon: <AirlineSeatIndividualSuiteOutlinedIcon />,
      },
      {
        title: "Leave Credits",
        to: "/admin/dashboard/credit",
        icon: <EnergySavingsLeafIcon />,
      },
    ],
  },
  {
    id: "assets",
    label: "Assets",
    icon: <InventoryOutlinedIcon />,
    items: [
      {
        title: "Main Assets Register",
        to: "/admin/dashboard/inventory",
        icon: <HandymanIcon />,
      },
      {
        title: "FAR Auditing",
        to: "/admin/dashboard/audit",
        icon: <VerifiedUserIcon />,
      },
      {
        title: "Consumable Register",
        to: "/admin/dashboard/distribution",
        icon: <Inventory2OutlinedIcon />,
      },
      {
        title: "Equipment Borrowing",
        to: "/admin/dashboard/borrow",
        icon: <LaptopMacOutlinedIcon />,
      },
    ],
  },
  {
    id: "pages",
    label: "Pages",
    icon: <ArticleOutlinedIcon />,
    items: [
      {
        title: "Calendar",
        to: "/admin/dashboard/calender",
        icon: <CalendarTodayOutlinedIcon />,
      },
      {
        title: "Vacancies",
        to: "/admin/dashboard/contact/carear",
        icon: <WorkOutlineOutlinedIcon />,
      },
    ],
  },
];

const SideBar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const location = useLocation();

  const [isCollapsed, setIsCollapsed] = useState(true);
  const [openGroups, setOpenGroups] = useState({});

  /* ---------------- Derived design tokens ---------------- */
  const ui = useMemo(() => {
    const accent = colors.teal[500];
    const accent2 = colors.newTeal[500];
    return {
      accent,
      accent2,
      surface: isDark ? colors.dark[700] : colors.white[900],
      surfaceAlt: isDark ? colors.dark[600] : colors.white[600],
      border: isDark
        ? alpha(colors.white[500], 0.09)
        : alpha(colors.dark[500], 0.1),
      textPrimary: colors.primary[100],
      textSecondary: isDark ? colors.gray[400] : colors.gray[300],
      gradient: `linear-gradient(135deg, ${accent} 0%, ${accent2} 100%)`,
    };
  }, [colors, isDark]);

  /* ---------------- Active route ---------------- */
  const isActive = useCallback(
    (to) => location.pathname === to || location.pathname.startsWith(`${to}/`),
    [location.pathname]
  );

  const activeGroupId = useMemo(
    () => NAV_GROUPS.find((g) => g.items.some((i) => isActive(i.to)))?.id || null,
    [isActive]
  );

  // Expand whichever group owns the current route.
  useEffect(() => {
    if (activeGroupId) {
      setOpenGroups((prev) => ({ ...prev, [activeGroupId]: true }));
    }
  }, [activeGroupId]);

  const toggleGroup = (id, open) =>
    setOpenGroups((prev) => ({ ...prev, [id]: open }));

  /* ---------------- react-pro-sidebar styling ---------------- */
  const menuItemStyles = {
    root: {
      fontSize: 13.5,
      fontWeight: 600,
    },
    icon: ({ active }) => ({
      color: active ? ui.accent : ui.textSecondary,
      marginRight: isCollapsed ? 0 : 10,
      transition: "color .18s ease",
    }),
    SubMenuExpandIcon: {
      color: ui.textSecondary,
    },
    subMenuContent: {
      backgroundColor: isCollapsed ? ui.surface : "transparent",
      borderRadius: isCollapsed ? 10 : 0,
    },
    button: ({ level, active }) => ({
      height: 44,
      margin: "2px 10px",
      borderRadius: 10,
      paddingLeft: level === 0 ? 14 : isCollapsed ? 14 : 26,
      color: active ? ui.accent : ui.textSecondary,
      backgroundColor: active ? alpha(ui.accent, isDark ? 0.16 : 0.12) : "transparent",
      transition: "background-color .18s ease, color .18s ease",
      "&:hover": {
        color: ui.accent,
        backgroundColor: alpha(ui.accent, isDark ? 0.12 : 0.09),
      },
    }),
    label: {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  };

  const SectionLabel = ({ children }) =>
    isCollapsed ? (
      <Box sx={{ height: 14 }} />
    ) : (
      <Typography
        variant="h6"
        sx={{
          m: "16px 0 6px 24px",
          fontSize: 10.5,
          fontWeight: 800,
          letterSpacing: 1.1,
          textTransform: "uppercase",
          color: alpha(ui.textSecondary, 0.85),
        }}
      >
        {children}
      </Typography>
    );

  return (
    <Sidebar
      collapsed={isCollapsed}
      width="270px"
      collapsedWidth="82px"
      rootStyles={{
        height: "100vh",
        border: "none",
        borderRight: `1px solid ${ui.border}`,
        "& .ps-sidebar-container": {
          background: ui.surface,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            background: alpha(ui.textSecondary, 0.3),
            borderRadius: 3,
          },
        },
      }}
    >
      {/* ---------------------------------------------------------------- */}
      {/* Header                                                            */}
      {/* ---------------------------------------------------------------- */}
      <Box
        sx={{
          px: isCollapsed ? 0 : 2.25,
          py: 2,
          mb: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: isCollapsed ? "center" : "space-between",
          borderBottom: `1px solid ${ui.border}`,
        }}
      >
        {!isCollapsed && (
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                letterSpacing: -0.3,
                background: ui.gradient,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              ADMIN
            </Typography>
            <Typography variant="h6" sx={{ fontSize: 11, color: ui.textSecondary }}>
              Mindsinaction
            </Typography>
          </Box>
        )}

        <Tooltip title={isCollapsed ? "Expand menu" : "Collapse menu"} placement="right">
          <IconButton
            onClick={() => setIsCollapsed((prev) => !prev)}
            sx={{
              borderRadius: 2,
              color: ui.textSecondary,
              border: `1px solid ${ui.border}`,
              "&:hover": {
                color: ui.accent,
                borderColor: alpha(ui.accent, 0.5),
                bgcolor: alpha(ui.accent, 0.08),
              },
            }}
          >
            {isCollapsed ? (
              <MenuOutlinedIcon fontSize="small" />
            ) : (
              <MenuOpenOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* ---------------------------------------------------------------- */}
      {/* Menu                                                              */}
      {/* ---------------------------------------------------------------- */}
      <Menu menuItemStyles={menuItemStyles} closeOnClick>
        <MenuItem
          icon={<DashboardOutlinedIcon />}
          active={isActive("/admin/dashboard")}
          component={<Link to="/admin/dashboard" />}
        >
          Dashboard
        </MenuItem>

        <SectionLabel>Workspace</SectionLabel>

        {NAV_GROUPS.map((group) => (
          <SubMenu
            key={group.id}
            label={group.label}
            icon={group.icon}
            open={!!openGroups[group.id]}
            onOpenChange={(open) => toggleGroup(group.id, open)}
            // Highlight the parent when one of its children is the current page
            // but the group is closed, so the active route is never invisible.
            rootStyles={
              group.id === activeGroupId && !openGroups[group.id]
                ? { "& > .ps-menu-button": { color: `${ui.accent} !important` } }
                : undefined
            }
          >
            {group.items.map((item) => (
              <MenuItem
                key={item.to}
                icon={item.icon}
                active={isActive(item.to)}
                component={<Link to={item.to} />}
              >
                {item.title}
              </MenuItem>
            ))}
          </SubMenu>
        ))}

        <SectionLabel>External</SectionLabel>

        {/* A real anchor — react-router's Link cannot navigate off-site. */}
        <MenuItem
          icon={<ForwardToInboxOutlinedIcon />}
          component={
            <a href={WEBMAIL_URL} target="_blank" rel="noopener noreferrer">
              {}
            </a>
          }
          suffix={
            !isCollapsed && (
              <OpenInNewIcon sx={{ fontSize: 14, color: ui.textSecondary }} />
            )
          }
        >
          Send Emails
        </MenuItem>
      </Menu>

      <Box sx={{ flex: 1 }} />

      {!isCollapsed && (
        <Box sx={{ px: 2.5, py: 2, borderTop: `1px solid ${ui.border}` }}>
          <Typography variant="h6" sx={{ fontSize: 10.5, color: ui.textSecondary }}>
            © {new Date().getFullYear()} Mindsinaction
          </Typography>
        </Box>
      )}
    </Sidebar>
  );
};

export default SideBar;